// ============================================================
// frontend/src/components/Toast.tsx
// ============================================================
// Notification toast at the bottom of the screen. Auto-dismisses.
//
// Used app-wide via the `useToast` hook (in App.tsx).
// ============================================================

import { useEffect } from 'react';
import { AlertCircle, CheckCircle, X } from 'lucide-react';

interface ToastProps {
  message: string;
  type?: 'error' | 'success';
  onDismiss: () => void;
  durationMs?: number;
}

export function Toast({ message, type = 'error', onDismiss, durationMs = 4000 }: ToastProps) {
  useEffect(() => {
    if (!message) return;
    const t = setTimeout(onDismiss, durationMs);
    return () => clearTimeout(t);
  }, [message, durationMs, onDismiss]);

  if (!message) return null;

  const Icon = type === 'success' ? CheckCircle : AlertCircle;
  return (
    <div className={`toast is-${type}`}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <Icon size={16} />
        <span>{message}</span>
        <button
          onClick={onDismiss}
          aria-label="Dismiss"
          style={{
            background: 'none', border: 'none', color: 'inherit',
            marginLeft: 8, cursor: 'pointer', padding: 4, display: 'flex',
          }}
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}
